import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import chatApi from "@/lib/chatApi";

const ChatAuthContext = createContext(null);

export function ChatAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // If returning from Emergent OAuth, AuthCallback handles it — skip /me
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await chatApi.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const persistToken = (token) => {
    if (token) localStorage.setItem("vg_chat_token", token);
  };

  const login = async (email, password) => {
    try {
      const { data } = await chatApi.post("/auth/login", { email, password });
      persistToken(data.session_token);
      setUser(data.user);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatApiErr(err) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await chatApi.post("/auth/register", { email, password, name });
      persistToken(data.session_token);
      setUser(data.user);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatApiErr(err) };
    }
  };

  const exchangeEmergentSession = async (sessionId) => {
    const { data } = await chatApi.post("/auth/session", null, { headers: { "X-Session-ID": sessionId } });
    persistToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await chatApi.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("vg_chat_token");
    setUser(null);
  };

  return (
    <ChatAuthContext.Provider value={{ user, loading, login, register, logout, refresh, exchangeEmergentSession }}>
      {children}
    </ChatAuthContext.Provider>
  );
}

export const useChatAuth = () => useContext(ChatAuthContext);

function formatApiErr(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  if (d?.msg) return d.msg;
  return err?.message || "Something went wrong";
}
